import React, { useMemo, useRef, useState } from 'react';
import { CheckCircle2, History, PackageCheck, Truck } from 'lucide-react';
import { Button } from '../../Button';
import { describeError } from '../../../services/errorMessage';
import {
    STATUS_KNOPF_TEXT,
    VERLAUF_GRENZE,
    aufgefuelltText,
    bestelltText,
    erlaubteStatusZiele,
    gemeldetText,
    mailVermerk,
    statusChip,
    teileMeldungen,
    verlaufText,
    wiederholtText,
    type StatusZiel,
} from '../../../services/material/materialLogic';
import type { MaterialArtikel, MaterialMeldung } from '../../../services/material/materialTypes';
import { MailVermerkAnzeige, StatusChipAnzeige } from './MaterialChips';

/**
 * Reiter „Meldungen“: offene Vorgänge oben mit „Bestellt“ und „Aufgefüllt“,
 * darunter der Verlauf der zuletzt aufgefüllten. Karten statt Tabelle — der
 * Beschaffer öffnet das auch am Handy.
 */

interface Props {
    meldungen: MaterialMeldung[];
    artikel: MaterialArtikel[];
    verlaufGekappt: boolean;
    /** Bezug für „vor X Tagen“ (Zeitpunkt des letzten Ladens). */
    jetzt: Date;
    nurLesen: boolean;
    onStatus: (meldungId: string, ziel: StatusZiel) => Promise<unknown>;
}

/** So viele Verlaufszeilen zeigt der Reiter zunächst; „Weitere“ blendet mehr ein. */
const VERLAUF_SCHRITT = 20;

const ZIEL_ICON: Record<StatusZiel, React.ReactNode> = {
    bestellt: <Truck className="w-4 h-4" />,
    erledigt: <PackageCheck className="w-4 h-4" />,
};

export const MeldungenReiter: React.FC<Props> = ({ meldungen, artikel, verlaufGekappt, jetzt, nurLesen, onStatus }) => {
    const { offen, verlauf } = useMemo(() => teileMeldungen(meldungen), [meldungen]);
    const artikelNachId = useMemo(() => new Map(artikel.map(a => [a.id, a])), [artikel]);

    // Ein Klick zur Zeit: „meldungId:ziel“ des laufenden Knopfs.
    const [laeuft, setLaeuft] = useState<string | null>(null);
    const laeuftRef = useRef(false);
    const [fehler, setFehler] = useState<{ id: string; text: string } | null>(null);
    const [verlaufZahl, setVerlaufZahl] = useState(VERLAUF_SCHRITT);

    const setzen = async (m: MaterialMeldung, ziel: StatusZiel) => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        setLaeuft(`${m.id}:${ziel}`);
        setFehler(null);
        try {
            await onStatus(m.id, ziel);
        } catch (e) {
            setFehler({ id: m.id, text: describeError(e) });
        } finally {
            laeuftRef.current = false;
            setLaeuft(null);
        }
    };

    return (
        <div className="space-y-6">
            <section aria-labelledby="material-offen-titel" className="space-y-2">
                <h3 id="material-offen-titel" className="text-[11px] font-bold text-slate-400 uppercase">
                    Offen ({offen.length})
                </h3>
                {offen.length === 0 ? (
                    <p className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> Keine offenen Meldungen.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {offen.map(m => {
                            const a = artikelNachId.get(m.artikelId);
                            const wiederholt = wiederholtText(m, jetzt);
                            const bestellt = bestelltText(m, jetzt);
                            const ziele = nurLesen ? [] : erlaubteStatusZiele(m.status);
                            return (
                                <li key={m.id} data-glass="karte" className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="font-bold text-slate-800 break-words">
                                                {a?.name ?? 'Unbekannter Artikel'}
                                                {a && !a.aktiv && <span className="ml-2 text-[11px] font-semibold text-slate-400">deaktiviert</span>}
                                            </p>
                                            {a?.lagerort && <p className="text-xs text-slate-500 break-words">{a.lagerort}</p>}
                                        </div>
                                        <StatusChipAnzeige chip={statusChip(m.status)} />
                                    </div>
                                    <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                                        <p><span className="text-slate-400">Gemeldet:</span> {gemeldetText(m, jetzt)}</p>
                                        {wiederholt && <p className="font-semibold text-amber-800">{wiederholt}</p>}
                                        {bestellt && <p>{bestellt}</p>}
                                        <p><MailVermerkAnzeige vermerk={mailVermerk(m)} /></p>
                                    </div>
                                    {ziele.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {ziele.map(ziel => (
                                                <Button
                                                    key={ziel}
                                                    variant={ziel === 'erledigt' ? 'primary' : 'secondary'}
                                                    className="min-h-[44px]"
                                                    icon={ZIEL_ICON[ziel]}
                                                    loading={laeuft === `${m.id}:${ziel}`}
                                                    disabled={laeuft !== null}
                                                    onClick={() => { void setzen(m, ziel); }}
                                                >
                                                    {STATUS_KNOPF_TEXT[ziel]}
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                    {fehler?.id === m.id && <p role="alert" className="mt-2 text-xs text-red-700">{fehler.text}</p>}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <section aria-labelledby="material-verlauf-titel" className="space-y-2">
                <h3 id="material-verlauf-titel" className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400 uppercase">
                    <History className="w-3.5 h-3.5" /> Verlauf ({verlauf.length}{verlaufGekappt ? '+' : ''})
                </h3>
                {verlauf.length === 0 ? (
                    <p className="text-xs text-slate-400">Noch keine aufgefüllten Meldungen.</p>
                ) : (
                    <>
                        <ul data-glass="karte" className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
                            {verlauf.slice(0, verlaufZahl).map(m => {
                                const a = artikelNachId.get(m.artikelId);
                                return (
                                    <li key={m.id} className="px-3 py-2 text-xs text-slate-600">
                                        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                                            <span className="font-semibold text-slate-800 break-words">{a?.name ?? 'Unbekannter Artikel'}</span>
                                            <span className="tabular-nums">{aufgefuelltText(m, jetzt) ?? 'Aufgefüllt'}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400">{verlaufText(m, jetzt)}</p>
                                    </li>
                                );
                            })}
                        </ul>
                        {verlauf.length > verlaufZahl && (
                            <Button variant="ghost" size="sm" onClick={() => setVerlaufZahl(z => z + 50)}>
                                Weitere anzeigen ({verlauf.length - verlaufZahl})
                            </Button>
                        )}
                        {verlaufGekappt && verlauf.length <= verlaufZahl && (
                            <p className="text-[11px] text-slate-400">Gezeigt werden die letzten {VERLAUF_GRENZE} aufgefüllten Meldungen.</p>
                        )}
                    </>
                )}
            </section>
        </div>
    );
};
